import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchMeterReports,
  fetchMeterReplacementDrafts,
  deleteMeterReport,
  deleteMeterReplacementDraft,
} from "../../data/fetchData";
import {
  FaTrashAlt,
  FaEye,
  FaEdit,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaSearch,
} from "react-icons/fa";
import { registerLocale } from "react-datepicker";
import { et } from "date-fns/locale";

registerLocale("et", et);

function Works({ currentUser }) {
  const [works, setWorks] = useState([]);
  const [replacementDrafts, setReplacementDrafts] = useState([]);
  const [localDrafts, setLocalDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "createdKey",
    direction: "descending",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [currentUserState, setCurrentUserState] = useState(null);
  const navigate = useNavigate();

  // Function to load localStorage drafts
  const loadLocalDrafts = () => {
    const drafts = [];
    
    // Check for Add draft
    const addData = localStorage.getItem("addData");
    if (addData) {
      try {
        const addDraft = JSON.parse(addData);
        drafts.push({
          id: `local-add-${Date.now()}`,
          source: 'localStorage',
          workflow: 'add',
          status: 'DRAFT',
          action: 'add',
          meter_number: addDraft.tehaseNumber || 'N/A',
          estate_address: addDraft.aadress || 'N/A',
          client_name: addDraft.omanik || 'N/A',
          date: addDraft.draftSavedAt || addDraft.workDate,
          createdKey: addDraft.draftSavedAt || addDraft.workDate || new Date().toISOString(),
          draftData: addDraft,
          statusLabel: 'Mustand (localStorage)',
          actionLabel: 'Lisamine'
        });
      } catch (e) {
        console.error("Error parsing add draft:", e);
      }
    }

    // Check for Remove draft
    const removeData = localStorage.getItem("removeData");
    if (removeData) {
      try {
        const removeDraft = JSON.parse(removeData);
        drafts.push({
          id: `local-remove-${Date.now()}`,
          source: 'localStorage',
          workflow: 'remove',
          status: 'DRAFT',
          action: 'remove',
          meter_number: removeDraft.meterNumber || 'N/A',
          estate_address: removeDraft.address || 'N/A',
          client_name: removeDraft.owner || 'N/A',
          date: removeDraft.draftSavedAt || removeDraft.removalDate,
          createdKey: removeDraft.draftSavedAt || removeDraft.removalDate || new Date().toISOString(),
          draftData: removeDraft,
          statusLabel: 'Mustand (localStorage)',
          actionLabel: 'Eemaldamine'
        });
      } catch (e) {
        console.error("Error parsing remove draft:", e);
      }
    }

    return drafts;
  };

  useEffect(() => {
    // Determine current user: prioritize prop, then localStorage
    const user = currentUser || localStorage.getItem("username");
    setCurrentUserState(user);

    if (!user) {
      console.warn("No current user found; no works will be displayed.");
      setWorks([]); // Empty array - no works shown
      setLocalDrafts([]);
      setLoading(false);
      return;
    }

    const loadBackendData = async () => {
      setLoading(true);
      try {
        const [reports, drafts] = await Promise.all([
          fetchMeterReports(),
          fetchMeterReplacementDrafts(),
        ]);

        const userWorks = Array.isArray(reports)
          ? reports.filter((w) => w.performed_by === user)
          : [];
        setWorks(userWorks);

        setReplacementDrafts(Array.isArray(drafts) ? drafts : []);
      } catch (err) {
        console.error("Error fetching works or replacement drafts:", err);
        setWorks([]);
        setReplacementDrafts([]);
      } finally {
        setLoading(false);
      }
    };

    loadBackendData();

    // Load localStorage drafts
    const localDrafts = loadLocalDrafts();
    setLocalDrafts(localDrafts);
  }, [currentUser]);

  // Listen for localStorage changes to refresh drafts when user returns to page
  useEffect(() => {
    const handleStorageChange = () => {
      const updatedDrafts = loadLocalDrafts();
      setLocalDrafts(updatedDrafts);
    };

    // Listen for storage events (from other tabs)
    window.addEventListener('storage', handleStorageChange);
    
    // Also check for changes when page becomes visible (user returns)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleStorageChange();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Status mapping helper
  const getStatusLabel = (status) => {
    switch (status) {
      case "SIGNED":
        return "Allkirjastatud";
      case "UNSIGNED":
        return "Kinnitatud";
      case "DRAFT":
        return "Mustand";
      default:
        return status;
    }
  };

  // Update the enrichedWorks memo to include localStorage drafts
  const enrichedWorks = React.useMemo(
    () => {
      // Process backend works
      const backendWorks = works.map((w) => {
        const createdKey = w.created_at || w.signed_date || w.date;
        return {
          ...w,
          // Preserve original w.type (meter type). Use separate field to mark source.
          source: 'backend',
          status: w.status || (w.signature ? "SIGNED" : "UNSIGNED"), // Fallback for older records
          statusLabel: getStatusLabel(
            w.status || (w.signature ? "SIGNED" : "UNSIGNED")
          ),
          actionLabel: w.action === "add" ? "Lisamine" : "Eemaldamine",
          createdKey,
        };
      });

      const replacementEntries = replacementDrafts.map((draft) => {
        const payload = draft.payload || {};
        const meterNumber = draft.old_meter_number && draft.new_meter_number
          ? `${draft.old_meter_number} → ${draft.new_meter_number}`
          : draft.old_meter_number || draft.new_meter_number || '—';
        const estateAddress =
          draft.estate_address ||
          payload.aadress ||
          payload.oldAddress ||
          'N/A';
        const clientName =
          draft.client_name ||
          payload.client_name ||
          payload.oldOwner ||
          'N/A';
        const createdKey = draft.updated_at || draft.created_at;

        return {
          ...draft,
          source: 'backend',
          workflow: 'replace',
          action: 'replace',
          actionLabel: 'Vahetus',
          status: draft.status || 'DRAFT',
          statusLabel: draft.status_label || 'Mustand',
          meter_number: meterNumber,
          estate_address: estateAddress,
          client_name: clientName,
          createdKey,
        };
      });

      // Combine backend works, replacement drafts, and localStorage drafts
      return [...backendWorks, ...replacementEntries, ...localDrafts];
    },
    [works, replacementDrafts, localDrafts]
  );

  // Update getRowBg function for different status colors
  const getRowBg = (work) => {
    if (work.source === 'localStorage' && work.status === 'DRAFT') {
      return "bg-blue-50 border-l-4 border-blue-400"; // Special styling for localStorage drafts
    }
    
    switch (work.status) {
      case "DRAFT":
        return "bg-gray-50";
      case "UNSIGNED":
        return "bg-yellow-50";
      case "SIGNED":
        return "bg-green-50";
      default:
        return "";
    }
  };

  // Update handleViewEdit to handle both backend and localStorage works
  const handleViewEdit = (work) => {
    if (work.status === "DRAFT") {
      if (work.source === 'localStorage') {
        // Handle localStorage drafts
        switch (work.workflow) {
          case 'add':
            navigate("/Add1");
            break;
          case 'remove':
            navigate("/Remove1");
            break;
          case 'replace':
            navigate("/Replace1");
            break;
          default:
            console.error("Unknown localStorage workflow:", work.workflow);
        }
      } else {
        // Handle backend drafts
        if (work.workflow === 'replace') {
          navigate(`/Replace1?draft=${work.id}`, {
            state: { replacementDraft: work },
          });
        } else if (work.action === "remove") {
          navigate("/Remove1", { state: { work } });
        } else {
          navigate("/Add1", { state: { work } });
        }
      }
    } else {
      // For SIGNED and UNSIGNED, only allow viewing
      navigate("/ViewWork", { state: { work } });
    }
  };

  const handleDelete = (work) => {
    if (
      !window.confirm("Kas olete kindel, et soovite selle aruande kustutada?")
    )
      return;

    if (work.source === 'localStorage') {
      // Delete localStorage draft
      const storageKey = work.workflow === 'add'
        ? 'addData'
        : work.workflow === 'remove'
        ? 'removeData'
        : null;
      
      if (storageKey) {
        localStorage.removeItem(storageKey);
        // Refresh localStorage drafts
        const updatedLocalDrafts = loadLocalDrafts();
        setLocalDrafts(updatedLocalDrafts);
      }
    } else {
      // Delete backend draft
      if (work.workflow === 'replace') {
        deleteMeterReplacementDraft(work.id)
          .then(() =>
            setReplacementDrafts((prev) =>
              prev.filter((draft) => draft.id !== work.id)
            )
          )
          .catch((err) => {
            console.error("Error deleting replacement draft:", err);
            alert("Vahetuse mustandi kustutamine ebaõnnestus.");
          });
        return;
      }

      deleteMeterReport(work.number)
        .then(() => setWorks((prev) => prev.filter((w) => w.number !== work.number)))
        .catch((err) => console.error("Error deleting work:", err));
    }
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedWorks = React.useMemo(() => {
    const key = sortConfig.key;
    const dir = sortConfig.direction === "ascending" ? 1 : -1;
    return [...enrichedWorks].sort((a, b) => {
      if (key === 'createdKey') {
        // Compare by creation time (backend created_at; local drafts have draftSavedAt/autoSavedAt/workDate)
        const at = new Date(a.createdKey || 0).getTime();
        const bt = new Date(b.createdKey || 0).getTime();
        if (at === bt) return 0;
        return at > bt ? dir * 1 : dir * -1;
      }
      const av = (a[key] ?? "").toString();
      const bv = (b[key] ?? "").toString();
      if (av === bv) return 0;
      return av > bv ? dir * 1 : dir * -1;
    });
  }, [enrichedWorks, sortConfig]);

  const filteredWorks = sortedWorks.filter((work) =>
    Object.values(work).some((val) =>
      String(val).toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <FaSort className="ml-1 text-gray-500" />;
    return sortConfig.direction === "ascending" ? (
      <FaSortUp className="ml-1 text-[var(--color-secondary)]" />
    ) : (
      <FaSortDown className="ml-1 text-[var(--color-secondary)]" />
    );
  };

  if (loading) return <p>Loading…</p>;

  return (
    <div className="max-w-screen-xl mx-auto p-2 sm:p-4 mt-16">
      <h2 className="text-lg font-semibold text-center mb-4 text-primary">
        Minu tööd
      </h2>

      {!currentUserState && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-yellow-800 text-sm">
            ⚠️ Kasutaja pole tuvastatud. Palun logi sisse, et näha oma töid.
          </p>
        </div>
      )}

      {localDrafts.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-blue-800 text-sm">
            <strong>💾 Leitud {localDrafts.length} localStorage-s asuvat mustandit.</strong> - 
            Need mustandid on salvestatud teie brauseris ja näidatakse sinise äärisega. 
            Klõpsake mustandile, et jätkata tööd.
          </p>
        </div>
      )}

      <div className="relative mb-4">
        {searchQuery === "" && (
          <FaSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500" />
        )}
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full p-2 pl-8 border border-gray-300 rounded-md"
          placeholder="Otsi…"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full table-auto border-collapse rounded-lg overflow-hidden">
          <thead className="bg-primary text-white">
            <tr>
              <th />
              {[
                { key: "statusLabel", label: "Staatus" },
                { key: "estate_address", label: "Aadress" },
                { key: "client_name", label: "Omanik" },
                { key: "meter_number", label: "Veemõõtja nr" },
                { key: "createdKey", label: "Kuupäev" },
                { key: "actionLabel", label: "Töö liik" },
              ].map((col) => (
                <th
                  key={col.key}
                  className="px-2 py-2 text-left cursor-pointer"
                  onClick={() => handleSort(col.key)}
                >
                  <div className="flex items-center">
                    {col.label}
                    {renderSortIcon(col.key)}
                  </div>
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {filteredWorks.map((work) => (
              <tr
                key={work.id || work.number}
                className={`${getRowBg(work)} cursor-pointer hover:bg-gray-100 transition-colors`}
                onClick={() => handleViewEdit(work)}
              >
                <td className="px-2 py-2 text-center">
                  {work.status === "DRAFT" ? (
                    <div className="text-pink-600">
                      <FaEdit size={16} />
                    </div>
                  ) : (
                    <div className="text-primary">
                      <FaEye size={16} />
                    </div>
                  )}
                </td>
                <td className="px-2 py-2">{work.statusLabel}</td>
                <td className="px-2 py-2">{work.estate_address}</td>
                <td className="px-2 py-2">{work.client_name}</td>
                <td className="px-2 py-2">{work.meter_number}</td>
                <td className="px-2 py-2">
                  {(work.signed_date || work.date)
                    ? new Date(work.signed_date || work.date).toLocaleDateString("et-EE", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                    : ""}
                </td>
                <td className="px-2 py-2">{work.actionLabel}</td>
                <td className="px-2 py-2 text-center">
                  {work.status === "DRAFT" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent row click when delete button is clicked
                        handleDelete(work);
                      }}
                      className="text-rose-600 hover:text-rose-800 transition-colors"
                      title={work.source === 'localStorage' ? 'Kustuta localStorage mustand' : 'Kustuta backend mustand'}
                    >
                      <FaTrashAlt size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Works;
